import React, { useState } from 'react';
import { SettingsCard } from './shared/SettingsCard';
import { CreditCard, DollarSign, Gift, Tag, Percent, Clock, Plus, Trash2, Edit } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
interface SubscriptionSectionProps {
  setHasUnsavedChanges: (hasChanges: boolean) => void;
}
export const SubscriptionSection: React.FC<SubscriptionSectionProps> = ({
  setHasUnsavedChanges
}) => {
  const [subscription, setSubscription] = useState({
    price: 9.99,
    freeTrialEnabled: true,
    freeTrialDays: 7,
    bundles: [{
      id: 1,
      months: 3,
      discount: 10,
      price: 26.97
    }, {
      id: 2,
      months: 6,
      discount: 15,
      price: 50.95
    }, {
      id: 3,
      months: 12,
      discount: 25,
      price: 89.91
    }],
    freeTrialLinks: [{
      id: 1,
      code: 'WELCOME50',
      days: 7,
      maxUses: 50,
      usedCount: 12,
      expiryDate: '2023-12-31'
    }, {
      id: 2,
      code: 'NEWCONTENT',
      days: 3,
      maxUses: 100,
      usedCount: 45,
      expiryDate: '2023-11-30'
    }],
    trackingLinks: [{
      id: 1,
      name: 'Twitter Campaign',
      code: 'TW2023',
      clicks: 156,
      conversions: 23,
      revenue: 229.77
    }, {
      id: 2,
      name: 'Instagram Bio',
      code: 'IGBIO',
      clicks: 89,
      conversions: 12,
      revenue: 119.88
    }]
  });
  const [showAddBundleModal, setShowAddBundleModal] = useState(false);
  const [showAddTrialModal, setShowAddTrialModal] = useState(false);
  const [showAddTrackingModal, setShowAddTrackingModal] = useState(false);
  const [newBundle, setNewBundle] = useState({
    months: 3,
    discount: 10
  });
  const [newTrial, setNewTrial] = useState({
    code: '',
    days: 7,
    maxUses: 50,
    expiryDate: ''
  });
  const [newTracking, setNewTracking] = useState({
    name: '',
    code: ''
  });
  const handlePriceChange = (value: string) => {
    const price = parseFloat(value);
    if (!isNaN(price)) {
      setSubscription(prev => ({
        ...prev,
        price
      }));
      setHasUnsavedChanges(true);
    }
  };
  const handleToggleTrial = (checked: boolean) => {
    setSubscription(prev => ({
      ...prev,
      freeTrialEnabled: checked
    }));
    setHasUnsavedChanges(true);
  };
  const handleTrialDaysChange = (value: string) => {
    const days = parseInt(value);
    if (!isNaN(days) && days > 0) {
      setSubscription(prev => ({
        ...prev,
        freeTrialDays: days
      }));
      setHasUnsavedChanges(true);
    }
  };
  const handleAddBundle = () => {
    const price = subscription.price * newBundle.months * (1 - newBundle.discount / 100);
    const newBundleItem = {
      id: Date.now(),
      months: newBundle.months,
      discount: newBundle.discount,
      price: parseFloat(price.toFixed(2))
    };
    setSubscription(prev => ({
      ...prev,
      bundles: [...prev.bundles, newBundleItem]
    }));
    setNewBundle({
      months: 3,
      discount: 10
    });
    setShowAddBundleModal(false);
    setHasUnsavedChanges(true);
  };
  const handleDeleteBundle = (id: number) => {
    setSubscription(prev => ({
      ...prev,
      bundles: prev.bundles.filter(bundle => bundle.id !== id)
    }));
    setHasUnsavedChanges(true);
  };
  const handleAddTrial = () => {
    if (newTrial.code && newTrial.days > 0 && newTrial.maxUses > 0 && newTrial.expiryDate) {
      const newTrialItem = {
        id: Date.now(),
        code: newTrial.code,
        days: newTrial.days,
        maxUses: newTrial.maxUses,
        usedCount: 0,
        expiryDate: newTrial.expiryDate
      };
      setSubscription(prev => ({
        ...prev,
        freeTrialLinks: [...prev.freeTrialLinks, newTrialItem]
      }));
      setNewTrial({
        code: '',
        days: 7,
        maxUses: 50,
        expiryDate: ''
      });
      setShowAddTrialModal(false);
      setHasUnsavedChanges(true);
    }
  };
  const handleDeleteTrial = (id: number) => {
    setSubscription(prev => ({
      ...prev,
      freeTrialLinks: prev.freeTrialLinks.filter(trial => trial.id !== id)
    }));
    setHasUnsavedChanges(true);
  };
  const handleAddTracking = () => {
    if (newTracking.name && newTracking.code) {
      const newTrackingItem = {
        id: Date.now(),
        name: newTracking.name,
        code: newTracking.code,
        clicks: 0,
        conversions: 0,
        revenue: 0
      };
      setSubscription(prev => ({
        ...prev,
        trackingLinks: [...prev.trackingLinks, newTrackingItem]
      }));
      setNewTracking({
        name: '',
        code: ''
      });
      setShowAddTrackingModal(false);
      setHasUnsavedChanges(true);
    }
  };
  const handleDeleteTracking = (id: number) => {
    setSubscription(prev => ({
      ...prev,
      trackingLinks: prev.trackingLinks.filter(tracking => tracking.id !== id)
    }));
    setHasUnsavedChanges(true);
  };
  return <div className="divide-y divide-gray-200 dark:divide-gray-800" data-id="element-18973">
      <SettingsCard title="Subscription Price" description="Set your monthly subscription price and free trial options." data-id="element-18974">
        <div className="space-y-6" data-id="element-18975">
          {/* Monthly Price */}
          <div data-id="element-18976">
            <label htmlFor="subscription-price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18977">
              Monthly Subscription Price
            </label>
            <div className="relative" data-id="element-18978">
              <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-18979">
                <DollarSign size={16} className="text-gray-500" data-id="element-18980" />
              </div>
              <input type="number" id="subscription-price" value={subscription.price} onChange={e => handlePriceChange(e.target.value)} min="0" step="0.01" className="w-full pl-10 pr-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-purple-500" data-id="element-18981" />
            </div>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-18982">
              Set your monthly subscription price. You'll receive approximately
              80% after platform fees.
            </p>
          </div>
          {/* Free Trial */}
          <div data-id="element-18983">
            <div className="flex items-center mb-2" data-id="element-18984">
              <input type="checkbox" id="free-trial-toggle" checked={subscription.freeTrialEnabled} onChange={e => handleToggleTrial(e.target.checked)} className="w-4 h-4 text-pink-600 dark:text-purple-600 border-gray-300 dark:border-gray-700 rounded focus:ring-pink-500 dark:focus:ring-purple-500" data-id="element-18985" />
              <label htmlFor="free-trial-toggle" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-18986">
                Enable Free Trial for New Subscribers
              </label>
            </div>
            {subscription.freeTrialEnabled && <div className="mt-3" data-id="element-18987">
                <label htmlFor="trial-days" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-18988">
                  Free Trial Duration (Days)
                </label>
                <div className="relative" data-id="element-18989">
                  <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none" data-id="element-18990">
                    <Clock size={16} className="text-gray-500" data-id="element-18991" />
                  </div>
                  <input type="number" id="trial-days" value={subscription.freeTrialDays} onChange={e => handleTrialDaysChange(e.target.value)} min="1" max="30" className="w-full pl-10 pr-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-500 dark:focus:ring-purple-500" data-id="element-18992" />
                </div>
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-18993">
                  Recommended: 3-7 days. Longer trials may increase subscription
                  cancellations.
                </p>
              </div>}
          </div>
          {/* Subscription Bundles */}
          <div data-id="element-18994">
            <div className="flex items-center justify-between mb-3" data-id="element-18995">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-18996">
                Subscription Bundles
              </h4>
              <button className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded text-xs flex items-center gap-1.5" onClick={() => setShowAddBundleModal(true)} data-id="element-18997">
                <Plus size={14} data-id="element-18998" />
                <span data-id="element-18999">Add Bundle</span>
              </button>
            </div>
            {subscription.bundles.length > 0 ? <div className="space-y-3" data-id="element-19000">
                {subscription.bundles.map(bundle => <div key={bundle.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-19001">
                    <div data-id="element-19002">
                      <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-19003">
                        {bundle.months} Month{bundle.months > 1 ? 's' : ''}
                      </h5>
                      <div className="flex items-center gap-2 mt-1" data-id="element-19004">
                        <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-19005">
                          ${subscription.price.toFixed(2)} × {bundle.months}{' '}
                          months
                        </span>
                        <span className="px-1.5 py-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs rounded-full" data-id="element-19006">
                          {bundle.discount}% off
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3" data-id="element-19007">
                      <span className="font-medium text-gray-700 dark:text-gray-300" data-id="element-19008">
                        ${bundle.price.toFixed(2)}
                      </span>
                      <button className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" onClick={() => handleDeleteBundle(bundle.id)} data-id="element-19009">
                        <Trash2 size={16} data-id="element-19010" />
                      </button>
                    </div>
                  </div>)}
              </div> : <p className="text-sm text-gray-500 dark:text-gray-400 py-2" data-id="element-19011">
                No subscription bundles created yet.
              </p>}
          </div>
        </div>
      </SettingsCard>
      <SettingsCard title="Promotion & Marketing" description="Manage your promotional campaigns and tracking links." data-id="element-19012">
        <Tabs defaultValue="freeTrials" data-id="element-19013">
          <TabsList className="mb-4" data-id="element-19014">
            <TabsTrigger value="freeTrials" data-id="element-19015">Free Trial Links</TabsTrigger>
            <TabsTrigger value="tracking" data-id="element-19016">Tracking Links</TabsTrigger>
          </TabsList>
          <TabsContent value="freeTrials" data-id="element-19017">
            <div className="flex items-center justify-between mb-3" data-id="element-19018">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-19019">
                Free Trial Links
              </h4>
              <button className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded text-xs flex items-center gap-1.5" onClick={() => setShowAddTrialModal(true)} data-id="element-19020">
                <Plus size={14} data-id="element-19021" />
                <span data-id="element-19022">Create Trial Link</span>
              </button>
            </div>
            {subscription.freeTrialLinks.length > 0 ? <div className="space-y-3" data-id="element-19023">
                {subscription.freeTrialLinks.map(trial => <div key={trial.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-19024">
                    <div className="flex items-center justify-between" data-id="element-19025">
                      <div className="flex items-center gap-2" data-id="element-19026">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-purple-100 dark:bg-purple-900/30" data-id="element-19027">
                          <Gift size={16} className="text-purple-500" data-id="element-19028" />
                        </div>
                        <div data-id="element-19029">
                          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-19030">
                            {trial.code}
                          </h5>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-19031">
                            {trial.days} day trial • Expires: {trial.expiryDate}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-19032">
                        <button className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" onClick={() => {
                    // Copy link to clipboard
                    navigator.clipboard.writeText(`https://sinshift.com/subscribe?code=${trial.code}`);
                  }} data-id="element-19033">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-19034">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" data-id="element-19035"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" data-id="element-19036"></path>
                          </svg>
                        </button>
                        <button className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" onClick={() => handleDeleteTrial(trial.id)} data-id="element-19037">
                          <Trash2 size={16} data-id="element-19038" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2" data-id="element-19039">
                      <div className="p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700" data-id="element-19040">
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-19041">
                          Uses
                        </p>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-19042">
                          {trial.usedCount} / {trial.maxUses}
                        </p>
                      </div>
                      <div className="p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700" data-id="element-19043">
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-19044">
                          Conversion Rate
                        </p>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-19045">
                          {trial.usedCount > 0 ? `${Math.round(trial.usedCount / trial.maxUses * 100)}%` : '0%'}
                        </p>
                      </div>
                      <div className="p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700" data-id="element-19046">
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-19047">
                          Status
                        </p>
                        <p className="text-sm font-medium text-green-600 dark:text-green-400" data-id="element-19048">
                          Active
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div> : <p className="text-sm text-gray-500 dark:text-gray-400 py-2" data-id="element-19049">
                No free trial links created yet.
              </p>}
          </TabsContent>
          <TabsContent value="tracking" data-id="element-19050">
            <div className="flex items-center justify-between mb-3" data-id="element-19051">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-19052">
                Tracking Links
              </h4>
              <button className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded text-xs flex items-center gap-1.5" onClick={() => setShowAddTrackingModal(true)} data-id="element-19053">
                <Plus size={14} data-id="element-19054" />
                <span data-id="element-19055">Create Tracking Link</span>
              </button>
            </div>
            {subscription.trackingLinks.length > 0 ? <div className="space-y-3" data-id="element-19056">
                {subscription.trackingLinks.map(tracking => <div key={tracking.id} className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-19057">
                    <div className="flex items-center justify-between" data-id="element-19058">
                      <div className="flex items-center gap-2" data-id="element-19059">
                        <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30" data-id="element-19060">
                          <Tag size={16} className="text-blue-500" data-id="element-19061" />
                        </div>
                        <div data-id="element-19062">
                          <h5 className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-19063">
                            {tracking.name}
                          </h5>
                          <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-19064">
                            Code: {tracking.code}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2" data-id="element-19065">
                        <button className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" onClick={() => {
                    // Copy link to clipboard
                    navigator.clipboard.writeText(`https://sinshift.com/subscribe?ref=${tracking.code}`);
                  }} data-id="element-19066">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-19067">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" data-id="element-19068"></rect>
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" data-id="element-19069"></path>
                          </svg>
                        </button>
                        <button className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300" onClick={() => handleDeleteTracking(tracking.id)} data-id="element-19070">
                          <Trash2 size={16} data-id="element-19071" />
                        </button>
                      </div>
                    </div>
                    <div className="mt-2 grid grid-cols-3 gap-2" data-id="element-19072">
                      <div className="p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700" data-id="element-19073">
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-19074">
                          Clicks
                        </p>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-19075">
                          {tracking.clicks}
                        </p>
                      </div>
                      <div className="p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700" data-id="element-19076">
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-19077">
                          Conversions
                        </p>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-19078">
                          {tracking.conversions}
                        </p>
                      </div>
                      <div className="p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-700" data-id="element-19079">
                        <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-19080">
                          Revenue
                        </p>
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-19081">
                          ${tracking.revenue.toFixed(2)}
                        </p>
                      </div>
                    </div>
                  </div>)}
              </div> : <p className="text-sm text-gray-500 dark:text-gray-400 py-2" data-id="element-19082">
                No tracking links created yet.
              </p>}
          </TabsContent>
        </Tabs>
      </SettingsCard>
      <SettingsCard title="Payment Information" description="Set up your payment methods to receive earnings." data-id="element-19083">
        <div className="flex items-center justify-between" data-id="element-19084">
          <div data-id="element-19085">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2" data-id="element-19086">
              <CreditCard size={16} className="text-gray-500" data-id="element-19087" />
              Payment Method
            </h4>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400" data-id="element-19088">
              Connect your bank account or payment method to receive your
              earnings.
            </p>
          </div>
          <button className="px-3 py-1.5 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white rounded text-sm font-medium" data-id="element-19089">
            Set Up Payment
          </button>
        </div>
      </SettingsCard>
      {/* Add Bundle Modal */}
      {showAddBundleModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-id="element-19090">
          <div className="bg-white dark:bg-gray-900 p-6 rounded-xl max-w-md w-full" data-id="element-19091">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white" data-id="element-19092">
              Add Subscription Bundle
            </h3>
            <div className="space-y-4" data-id="element-19093">
              <div data-id="element-19094">
                <label htmlFor="bundle-months" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-19095">
                  Duration (Months)
                </label>
                <input type="number" id="bundle-months" value={newBundle.months} onChange={e => setNewBundle({
              ...newBundle,
              months: parseInt(e.target.value) || 3
            })} min="2" max="12" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" data-id="element-19096" />
              </div>
              <div data-id="element-19097">
                <label htmlFor="bundle-discount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-19098">
                  Discount (%)
                </label>
                <div className="relative" data-id="element-19099">
                  <input type="number" id="bundle-discount" value={newBundle.discount} onChange={e => setNewBundle({
                ...newBundle,
                discount: parseInt(e.target.value) || 0
              })} min="1" max="50" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" data-id="element-19100" />
                  <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none" data-id="element-19101">
                    <Percent size={16} className="text-gray-500" data-id="element-19102" />
                  </div>
                </div>
              </div>
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-lg" data-id="element-19103">
                <p className="text-sm text-gray-700 dark:text-gray-300" data-id="element-19104">
                  Bundle Summary:
                </p>
                <div className="mt-2 flex justify-between items-center" data-id="element-19105">
                  <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-19106">
                    ${subscription.price.toFixed(2)} × {newBundle.months} months
                    ({newBundle.discount}% off)
                  </span>
                  <span className="font-medium text-gray-700 dark:text-gray-300" data-id="element-19107">
                    $
                    {(subscription.price * newBundle.months * (1 - newBundle.discount / 100)).toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6" data-id="element-19108">
              <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm" onClick={() => setShowAddBundleModal(false)} data-id="element-19109">
                Cancel
              </button>
              <button className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white rounded-lg text-sm" onClick={handleAddBundle} data-id="element-19110">
                Add Bundle
              </button>
            </div>
          </div>
        </div>}
      {/* Add Trial Modal */}
      {showAddTrialModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-id="element-19111">
          <div className="bg-white dark:bg-gray-900 p-6 rounded-xl max-w-md w-full" data-id="element-19112">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white" data-id="element-19113">
              Create Free Trial Link
            </h3>
            <div className="space-y-4" data-id="element-19114">
              <div data-id="element-19115">
                <label htmlFor="trial-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-19116">
                  Promo Code
                </label>
                <input type="text" id="trial-code" value={newTrial.code} onChange={e => setNewTrial({
              ...newTrial,
              code: e.target.value.toUpperCase()
            })} placeholder="e.g. WELCOME50" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" data-id="element-19117" />
              </div>
              <div data-id="element-19118">
                <label htmlFor="trial-days" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-19119">
                  Trial Duration (Days)
                </label>
                <input type="number" id="trial-days" value={newTrial.days} onChange={e => setNewTrial({
              ...newTrial,
              days: parseInt(e.target.value) || 7
            })} min="1" max="30" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" data-id="element-19120" />
              </div>
              <div data-id="element-19121">
                <label htmlFor="trial-uses" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-19122">
                  Maximum Uses
                </label>
                <input type="number" id="trial-uses" value={newTrial.maxUses} onChange={e => setNewTrial({
              ...newTrial,
              maxUses: parseInt(e.target.value) || 50
            })} min="1" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" data-id="element-19123" />
              </div>
              <div data-id="element-19124">
                <label htmlFor="trial-expiry" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-19125">
                  Expiry Date
                </label>
                <input type="date" id="trial-expiry" value={newTrial.expiryDate} onChange={e => setNewTrial({
              ...newTrial,
              expiryDate: e.target.value
            })} className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" data-id="element-19126" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6" data-id="element-19127">
              <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm" onClick={() => setShowAddTrialModal(false)} data-id="element-19128">
                Cancel
              </button>
              <button className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white rounded-lg text-sm" onClick={handleAddTrial} disabled={!newTrial.code || !newTrial.expiryDate} data-id="element-19129">
                Create Link
              </button>
            </div>
          </div>
        </div>}
      {/* Add Tracking Modal */}
      {showAddTrackingModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-id="element-19130">
          <div className="bg-white dark:bg-gray-900 p-6 rounded-xl max-w-md w-full" data-id="element-19131">
            <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white" data-id="element-19132">
              Create Tracking Link
            </h3>
            <div className="space-y-4" data-id="element-19133">
              <div data-id="element-19134">
                <label htmlFor="tracking-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-19135">
                  Campaign Name
                </label>
                <input type="text" id="tracking-name" value={newTracking.name} onChange={e => setNewTracking({
              ...newTracking,
              name: e.target.value
            })} placeholder="e.g. Twitter Campaign" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" data-id="element-19136" />
              </div>
              <div data-id="element-19137">
                <label htmlFor="tracking-code" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1" data-id="element-19138">
                  Tracking Code
                </label>
                <input type="text" id="tracking-code" value={newTracking.code} onChange={e => setNewTracking({
              ...newTracking,
              code: e.target.value.toUpperCase()
            })} placeholder="e.g. TW2023" className="w-full px-3 py-2 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded-md" data-id="element-19139" />
              </div>
              <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-19140">
                Use tracking links to measure the performance of your marketing
                campaigns. You'll be able to see how many people click on your
                link and how many subscribe.
              </p>
            </div>
            <div className="flex justify-end gap-3 mt-6" data-id="element-19141">
              <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg text-sm" onClick={() => setShowAddTrackingModal(false)} data-id="element-19142">
                Cancel
              </button>
              <button className="px-4 py-2 bg-gradient-to-r from-pink-600 to-purple-600 hover:from-pink-700 hover:to-purple-700 text-white rounded-lg text-sm" onClick={handleAddTracking} disabled={!newTracking.name || !newTracking.code} data-id="element-19143">
                Create Link
              </button>
            </div>
          </div>
        </div>}
    </div>;
};